import cx from "clsx";
import SizingContainer from "common-utils/components/SizingContainer";
import { User } from "kreate-common/modules/business-types";
import ButtonLink from "kreate-common/modules/kreate-ui/components/ButtonLink";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import Link from "next/link";
import * as React from "react";
import { MdLanguage, MdOpenInNew } from "react-icons/md";

import { extractUrl } from "../../../PageUpdateProfile/fields/FieldSocialLinkItem/utils";
import Toolbar from "../../components/Toolbar";

import styles from "./index.module.scss";

import {
  formatInteger$Compact,
  formatInteger$Standard,
} from "@/modules/common-utils/format";
import { EM_DASH } from "@/modules/common-utils/unicode";
import { ImageViewer } from "@/modules/kreate-components/components/ImageViewer";
import PageLayout from "@/modules/kreate-components/components/PageLayout";
import IconFlagged from "@/modules/kreate-components/components/UserDisplayNameViewerV2/icons/IconFlagged";
import IconSuspended from "@/modules/kreate-components/components/UserDisplayNameViewerV2/icons/IconSuspended";
import IconVerified from "@/modules/kreate-components/components/UserDisplayNameViewerV2/icons/IconVerified";
import { useModalTrigger$ModalCollectorListViewer } from "@/modules/kreate-modals/modals/ModalCollectorListViewer/hooks";
import { getLinkToPageGalleryCollector } from "@/modules/navigation/utils/getLinkTo";
import { CHANNEL_KEY_TO_CHANNEL_INFO } from "@/modules/social-links/config";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  user: User | null | undefined;
};

export default function SectionHead({ className, style, user }: Props) {
  const modalTriggers = {
    ModalCollectorListViewer: useModalTrigger$ModalCollectorListViewer(),
  };

  const firstAddress = user?.walletAddresses?.[0];

  return (
    <div className={cx(styles.container, className)} style={style}>
      <PageLayout.Section>
        <Flex.Row justifyContent="space-between" gap="12px">
          <Flex.Cell
            className={styles.avatarContainer}
            alignSelf="stretch"
            flex="0 0 auto"
          >
            {user?.avatar ? (
              <SizingContainer
                className={styles.avatar}
                sizing={{ type: "fixed", width: "160px", height: "160px" }}
              >
                <ImageViewer
                  value={user?.avatar}
                  sizing="fill"
                  objectFit="cover"
                  sizes="160px"
                />
              </SizingContainer>
            ) : undefined}
          </Flex.Cell>
          <Toolbar user={user} />
        </Flex.Row>
        <Flex.Col flex="1 1 560px" gap="24px" paddingTop="20px">
          <h1 className={styles.displayName}>
            <span>{user?.displayName || EM_DASH}</span>
            {user?.isVerified ? (
              <IconVerified size="48px" style={{ marginLeft: "0.3em" }} />
            ) : null}
            {user?.flagWarning ? (
              <IconFlagged size="48px" style={{ marginLeft: "0.3em" }} />
            ) : null}
            {user?.flagSuspended ? (
              <IconSuspended size="48px" style={{ marginLeft: "0.3em" }} />
            ) : null}
          </h1>
          <Flex.Row alignItems="center" gap="20px" flexWrap="wrap">
            <ButtonLink
              className={styles.buttonLink}
              title={`${formatInteger$Standard(
                user?.numCollectors
              )} Collectors`}
              onClick={() =>
                user &&
                void modalTriggers.ModalCollectorListViewer.openModal({
                  userId: user.userId,
                })
              }
              alwaysUnderline
            >{`${formatInteger$Compact(
              user?.numCollectors
            )} Collectors`}</ButtonLink>
            {firstAddress ? (
              <>
                <hr className={styles.dashedVerticalDivider} />
                <Link
                  href={getLinkToPageGalleryCollector(firstAddress)}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.link}
                >
                  <span>{"Collector Gallery"}</span>
                  <MdOpenInNew size="24px" />
                </Link>
              </>
            ) : null}
            {user?.socialLinks.length ? (
              <>
                <hr className={styles.dashedVerticalDivider} />
                <Flex.Row gap="12px" alignItems="center" flexWrap="wrap">
                  {user.socialLinks.map((href, index) => {
                    const { host } = extractUrl(href);
                    const channelInfo = Object.values(
                      CHANNEL_KEY_TO_CHANNEL_INFO
                    ).find((channelInfo) =>
                      channelInfo.acceptedDomains.some(
                        (d) => d === host || host.endsWith("." + d)
                      )
                    );
                    return (
                      <Link
                        key={index}
                        href={href}
                        className={styles.iconContainer}
                        rel="noreferer"
                        target="_blank"
                      >
                        {channelInfo?.icon || <MdLanguage />}
                      </Link>
                    );
                  })}
                </Flex.Row>
              </>
            ) : null}
          </Flex.Row>
          {user?.headline ? (
            <p className={styles.headline}>{user.headline}</p>
          ) : null}
        </Flex.Col>
      </PageLayout.Section>
    </div>
  );
}
